/*
 * Consolidated React replacement for the share-tools section of
 * `part-actions.hbs` (footer) and `share-tools.hbs` (desktop sidebar).
 * Replaces three previously-separate concerns under one component:
 *
 * - The visible brand-icon share row (was an inline Handlebars
 *   `{{#each socialShareVisible}}` loop with brand-color CSS circles).
 * - The "more" button + dropdown (was `PartActionsMore` / legacy `<ul>`).
 * - The report flow (was the inline `ReportStoryPart` React injection).
 *
 * Variants:
 * - `variant="footer"`  — 32px icons, horizontal row, right-aligned menu.
 * - `variant="sidebar"` — 40px icons, vertical stack, menu opens upward.
 *
 * Click handling owned in React — no Backbone `tap .social-share`
 * delegation, no `data-share-*` attributes, no class-based hooks:
 * - Brand shares (facebook/twitter/pinterest/tumblr/email) call
 *   `openSharePopup` which opens a centered popup, fetches `/v4/link`
 *   for tracking params, then navigates the popup.
 * - Report opens the webview popup under `IS_NEW_REPORT_STORY_PART_FLOW`,
 *   else falls back to the legacy modal via `useReportModal`.
 * - Post-to-profile / embed fire `app.trigger('share:{name}:open')`;
 *   the corresponding Backbone view listens and opens its existing
 *   modal. This bridge is interim — full React modals are tracked as
 *   a follow-up.
 *
 * Gated behind `IS_REACT_PART_FOOTER_ACTIONS` in the .hbs templates.
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  SocialSharing,
  ActionMenu,
  Icon,
  IconNames
} from "@wattpad/web-ui-library";
import { useClickOutside, useReportModal, useTrans } from "../hooks";
import { openResponsivePopup, openSharePopup } from "../helpers";

const ICON_BY_NAME = {
  facebook: IconNames.WpFacebook,
  twitter: IconNames.WpXTwitter,
  pinterest: IconNames.WpPinterest,
  tumblr: IconNames.WpTumblr,
  // No WpEmail in the lib; WpSend (paper-airplane) is the closest fit
  // and matches the convention used elsewhere for `email` /
  // `post-to-profile` action-menu items.
  email: IconNames.WpSend,
  "post-to-profile": IconNames.WpSend,
  embed: IconNames.WpEmbed,
  report: IconNames.WpReport
};

const resolveIcon = name => ICON_BY_NAME[name] || IconNames.WpShare;

// Names that route to Backbone modals via `app.trigger`. These continue
// to use the legacy view-level modals until React modals are built.
// Array + .indexOf() rather than a Set — IE11 lacks Set in older builds.
const BACKBONE_MODAL_ITEMS = ["post-to-profile", "embed"];

const isBackboneModalItem = name => BACKBONE_MODAL_ITEMS.indexOf(name) !== -1;

const fireBackboneShareEvent = name => {
  window.app?.trigger?.(`share:${name}:open`);
};

const PartFooterActions = ({
  socialShareVisible,
  socialShareHidden,
  variant = "footer"
}) => {
  const { trans } = useTrans();
  const { openReportStoryModal } = useReportModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // `useClickOutside` expects a DOM node, not a React ref object — use the
  // callback-ref / state pattern (same shape Popover uses, see Popover.js).
  const [containerNode, setContainerNode] = useState(null);
  const triggerRef = useRef(null);
  const closeMenu = () => setIsMenuOpen(false);

  // Click-outside closes the menu — only attached while open. Passing
  // `null` for the node when closed makes the hook detach its listener.
  useClickOutside(isMenuOpen ? containerNode : null, closeMenu);

  // When the menu opens, move focus to the first menuitem so keyboard and
  // AT users can navigate it immediately (WAI-ARIA Menu Button pattern).
  useEffect(
    () => {
      if (!isMenuOpen || !containerNode) return;
      const firstItem = containerNode.querySelector('[role="menuitem"]');
      firstItem?.focus();
    },
    [isMenuOpen, containerNode]
  );

  // ESC closes the menu and returns focus to the trigger (WAI-ARIA Menu
  // Button pattern §3.15). Kept inline rather than reaching for
  // useTrapKeyboardNavigation, which would also add focus trapping —
  // out of scope for this PR.
  useEffect(
    () => {
      if (!isMenuOpen) return undefined;
      const handleKey = event => {
        if (event.key === "Escape") {
          closeMenu();
          triggerRef.current?.focus();
        }
      };
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    },
    [isMenuOpen]
  );

  const handleBrandShareClick = item => event => {
    // Backbone-modal items (embed, post-to-profile) can appear in
    // either row — embed lands in `socialShareVisible` (5th of 5 items
    // after splice), post-to-profile lands in `socialShareHidden`. Both
    // need to fire `app.trigger` instead of navigating to their stored
    // href (the iframe-embed URL / "/" respectively).
    // `event` is optional: this handler is reused as the menu's onClick
    // (see buildMenuOption's "navigable" branch), and ActionMenu's
    // MenuItem calls `onClick()` with no argument.
    if (isBackboneModalItem(item.name)) {
      event?.preventDefault();
      event?.stopPropagation();
      fireBackboneShareEvent(item.name);
      return;
    }
    // For nopopup items (`mailto:`, in-app links, etc.) or items without
    // a URL template, let the anchor's default `href` navigation handle
    // it — no preventDefault, no popup machinery.
    if (item.nopopup || !item.urlTemplate) return;
    event?.preventDefault();
    event?.stopPropagation();
    openSharePopup({
      urlTemplate: item.urlTemplate,
      url: item.url,
      text: item.text,
      content: item.content,
      channel: item.name
    });
  };

  // ActionMenu's MenuItem calls `onClick()` with no event arg, so the menu-side handlers
  // intentionally don't take or guard `event` — there's nothing to
  // preventDefault on a button click anyway. Brand shares from the
  // visible row do receive the event (handleBrandShareClick).
  const handleReportClick = item => () => {
    closeMenu();
    if (wattpad?.testGroups?.IS_NEW_REPORT_STORY_PART_FLOW) {
      openResponsivePopup({
        url: `/webview/report/storypart/${item.partId}`,
        width: 500,
        height: 700
      });
      return;
    }
    openReportStoryModal(
      item.storyId,
      item.storyTitle,
      item.author,
      item.language,
      item.partId
    );
  };

  const handleBackboneModalClick = item => () => {
    closeMenu();
    fireBackboneShareEvent(item.name);
  };

  const buildShareItem = item => ({
    name: item.name,
    icon: resolveIcon(item.name),
    label: item.label,
    // Pre-built href is preserved as a fallback for keyboard users who
    // open the link in a new tab (Ctrl+click / right-click) — they
    // bypass our onClick and need a working href.
    href: item.href,
    // SocialSharing defaults target to "_blank" when not set. Mirror the
    // legacy HBS which withheld target="_blank" on nopopup items (e.g.
    // mailto:) so they open in the same tab, not a new one.
    target: item.nopopup ? "_self" : "_blank",
    rel: item.nopopup ? undefined : "noopener noreferrer",
    // Preserve the analytics hook the legacy markup carried at
    // `part-actions.hbs:46` / `share-tools.hbs:62`. GTM triggers match
    // on this attribute — without it, share-click events from the
    // FF-on cohort go untracked during rollout.
    "data-gtm-action": `share:${item.name}`,
    onClick: handleBrandShareClick(item)
  });

  const buildMenuOption = item => {
    const base = {
      key: item.name,
      icon: resolveIcon(item.name),
      label: item.label,
      // See note on buildShareItem — preserves GTM tracking for share
      // events triggered from the more-menu (email, post-to-profile,
      // embed, report).
      dataGtmAction: `share:${item.name}`
    };
    if (item.name === "report") {
      // Report opens a popup or modal — no meaningful href; render as
      // a button.
      return { ...base, onClick: handleReportClick(item) };
    }
    if (isBackboneModalItem(item.name)) {
      // Post-to-profile / embed open Backbone modals via app.trigger.
      // Critically, we omit `href` here: the lib's ActionMenu MenuItem
      // calls `onClick` without passing the click event (type is
      // `() => void`), so our handler can't `preventDefault()` an
      // anchor's navigation. Rendering as a button (no href) sidesteps
      // it — there's no default navigation to prevent. Could be lifted
      // when the lib's MenuItem starts passing the event.
      return { ...base, onClick: handleBackboneModalClick(item) };
    }
    // Brand-style items in the more-menu (email, mailto:, etc.). Set
    // href so the anchor navigates as a fallback for non-popup items
    // (mailto:) and for Ctrl/middle-click in popup-able ones.
    const navigable = {
      ...base,
      href: item.href,
      target: item.nopopup ? "_self" : "_blank",
      rel: item.nopopup ? undefined : "noopener noreferrer"
    };
    const click = handleBrandShareClick(item);
    return {
      ...navigable,
      onClick: event => {
        closeMenu();
        click(event);
      }
    };
  };

  if (!socialShareVisible && !socialShareHidden) return null;

  // Match the visual footprint of the legacy brand-color circles
  // (~35px footer, 40px sidebar) rather than the bare FontAwesome glyph
  // sizes (18/24) those circles wrapped. The lib's SVG icons render at
  // exact pixel dimensions, so smaller numbers read as much smaller
  // without the background-circle padding the legacy CSS provided.
  const iconSize = variant === "sidebar" ? 40 : 32;
  const shareItems = (socialShareVisible || []).map(buildShareItem);
  const menuOptions = (socialShareHidden || []).map(buildMenuOption);

  return (
    <div
      ref={setContainerNode}
      className={classnames(
        "part-footer-actions",
        `part-footer-actions--${variant}`
      )}
    >
      {shareItems.length > 0 && (
        <SocialSharing
          items={shareItems}
          iconSize={iconSize}
          aria-label={trans("Share")}
          className="part-footer-actions__share-row"
        />
      )}
      {menuOptions.length > 0 && (
        <div className="part-footer-actions__more">
          <button
            type="button"
            ref={triggerRef}
            className="part-footer-actions__more-button"
            onClick={() => setIsMenuOpen(open => !open)}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            aria-label={trans("Open More Story Options")}
          >
            <Icon name={IconNames.WpMore} size={iconSize} ariaHidden />
          </button>
          {isMenuOpen && (
            <div className="part-footer-actions__menu">
              <ActionMenu
                options={menuOptions}
                aria-label={trans("More options")}
                data-testid="part-footer-actions-menu"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PartFooterActions.propTypes = {
  socialShareVisible: PropTypes.array,
  socialShareHidden: PropTypes.array,
  variant: PropTypes.oneOf(["footer", "sidebar"])
};

export default PartFooterActions;

// Thin wrappers fix the variant at the component-registration layer so
// the .hbs invocations don't need to pass props through (the
// `reactComponent` helper only passes the Handlebars context as model;
// there's no first-class way to add extra props from the call site).
export const PartFooterActionsFooter = props => (
  <PartFooterActions {...props} variant="footer" />
);

export const PartFooterActionsSidebar = props => (
  <PartFooterActions {...props} variant="sidebar" />
);
